import React, { useEffect, useState } from 'react';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { Button, Col, HasAccess, Popconfirm, Row, Space, Table, Tooltip, Typography } from 'components';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import {
  usePurchaseRequestByIdQuery,
  usePurchaseRequestItemAddMutation,
  usePurchaseRequestItemDeleteMutation,
  usePurchaseRequestItemUpdateMutation,
} from 'api';
import styles from './styles.module.scss';
import { EditableRow } from './EditableRow';
import { EditableCell } from './EditableCell';
import { SupplierPricesDrawer } from './SupplierPricesDrawer';
import { PurchaseRequestItem, PurchaseRequestItemsTableProps } from './types';
import { AddItemModal } from 'pages/admin/items/addItem';
import { useParams } from 'react-router-dom';
import { DeleteIcon } from 'components/common/icons';

const { Text } = Typography;
export const PurchaseRequestItemsTable: React.FC<PurchaseRequestItemsTableProps> = ({
  purchaseRequestId,
  brandId,
  initialItems,
  onSelectionChange,
  itemSelectorData,
}) => {
  const { id } = useParams() as { id: string };
  const [dataSource, setDataSource] = useState<PurchaseRequestItem[]>([]);
  const [count, setCount] = useState(0);
  const [openAddItem, setOpenAddItem] = useState(false);
  const { handleSuccess, handleError } = useNotificationMessages();
  const notificationApi = useNotificationApi();
  const { hasAccess } = useAccessControl();
  const [editingRow, setEditingRow] = useState<string>('');
  const [editingColumn, setEditingColumn] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<PurchaseRequestItem | null>(null);
  const [showPriceDrawer, setShowPriceDrawer] = useState(false);

  // API mutations
  const [updatePurchaseRequestItem, { isLoading }] = usePurchaseRequestItemUpdateMutation();
  const [addPurchaseRequestItem] = usePurchaseRequestItemAddMutation();
  const [deletePurchaseRequestItem, { isLoading: isDeleting }] = usePurchaseRequestItemDeleteMutation();
  const { data: purchaseRequestDetails = {} } = usePurchaseRequestByIdQuery({ id });

  const isFinalStatus = purchaseRequestDetails?.purchaseRequest?.isFinalStatus;
  const hasWriteAccess = hasAccess(PERMISSIONS.purchaseRequests.purchaseRequest.write) && !isFinalStatus;

  // Convert initialItems to the expected format and set as dataSource
  useEffect(() => {
    if (initialItems && Array.isArray(initialItems) && initialItems.length > 0) {
      try {
        const formattedItems = initialItems.map((item: any, index: number) => {
          // Ensure each item has the required properties with defaults if missing
          const formattedItem = {
            ...item,
            key: item.id || `item-${index}`,
            brandId: item.brandId,
            internalItemId: item.internalItemId || '',
            sku: item.sku || '',
            description: item.description || '',
            quantity: Number(item.quantity) || 0,
            // Handle both priceRange and priceRage (API typo) properties
            priceRange: item.priceRange || item.priceRage || null,
            // Set isSystemItem based on identified field being true
            isSystemItem: item.identified === true,
          };
          return formattedItem;
        });

        setDataSource(formattedItems);
        setCount(formattedItems.length);
      } catch (error) {
        console.error('Error processing items:', error);
        setDataSource([]);
      }
    } else {
      console.log('No items to process or items array is empty');
      setDataSource([]);
    }
  }, [initialItems]);

  const handleDelete = async (record: PurchaseRequestItem) => {
    if (!record.id) {
      // For newly added items that don't have an ID yet, just remove from local state
      const newData = dataSource.filter((item) => item.key !== record.key);
      setDataSource(newData);
      return;
    }

    try {
      // Call DELETE API
      const response = await deletePurchaseRequestItem({ id: record.id });

      if ('data' in response && response.data?.success) {
        handleSuccess('Item was successfully deleted');
        // Remove from local state after successful API call
        const newData = dataSource.filter((item) => item.id !== record.id);
        setDataSource(newData);
      } else if ('error' in response) {
        handleError(response.error);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const handleAdd = () => {
    const newItemKey = `new-${count}`;
    const newItem: PurchaseRequestItem = {
      id: '',
      key: newItemKey,
      internalItemId: '',
      internalItemUuid: '',
      sku: '',
      description: '',
      quantity: 0,
      brandId: '',
      isSystemItem: false,
    };

    // Add to the top of the table
    setDataSource([newItem, ...dataSource]);
    setCount(count + 1);

    // Set the newly added row and column to be edited
    setEditingRow(newItemKey);
    setEditingColumn('internalItemId');
  };

  const handleSave = async (row: PurchaseRequestItem) => {
    if (!isLoading) {
      try {
        // Check if required fields are provided
        if (!row.sku || row.sku.trim() === '') {
          notificationApi({ type: 'error', message: 'SKU is required' });
          return;
        }

        let response;

        if (row.id) {
          // UPDATE existing item
          const updatePayload = {
            id: row.id,
            sku: row.sku,
            description: row.description,
            quantity: Number(row.quantity) || 0,
          };

          response = await updatePurchaseRequestItem(updatePayload);
        } else {
          // ADD new item
          const addPayload = {
            purchaseRequestId,
            brandId: brandId || '',
            sku: row.sku,
            description: row.description,
            quantity: Number(row.quantity) || 0,
          };

          response = await addPurchaseRequestItem(addPayload);
        }

        if ('data' in response && response.data?.success) {
          //handleSuccess(row.id ? 'Item was successfully updated' : 'Item was successfully added');

          // Update the dataSource with the updated data
          const newData = [...dataSource];
          const index = newData.findIndex((item) => row.key === item.key);

          if (index > -1) {
            let responseData = response.data?.data;

            // If responseData is a string (like an ID), create a full object
            if (typeof responseData === 'string') {
              responseData = { id: responseData };
            }

            const updatedItem = {
              ...newData[index],
              ...row,
              ...responseData,
              id: responseData?.id || responseData || row.id,
              // Ensure we handle any field name mappings from the API response
              identified: responseData?.identified,
              isSystemItem: responseData?.identified === true,
            };

            newData.splice(index, 1, updatedItem);
            setDataSource(newData);
          }

          // Reset editing state
          setEditingRow('');
          setEditingColumn('');
        } else if ('error' in response) {
          handleError(response.error);
        }
      } catch (err) {
        handleError(err);
      }
    }
  };

  const handlePriceRangeClick = (record: PurchaseRequestItem) => {
    setSelectedItem(record);
    setShowPriceDrawer(true);
  };

  const renderItemSelector = (record: PurchaseRequestItem) => {
    return (
      <Space direction="vertical">
        {record.internalItemUuid}
        {record.sku}
        {record.isSystemItem === false && (
          <Tooltip title="Item not in system">
            <InfoCircleOutlined style={{ color: '#faad14' }} />
          </Tooltip>
        )}
      </Space>
    );
  };

  const columns: Record<string, any> = [
    {
      title: 'Item Number, SKU',
      dataIndex: 'internalItemId',
      editable: hasWriteAccess,
      width: '180px',
      inputType: 'itemSelector',
      render: (_: string, record: PurchaseRequestItem) => renderItemSelector(record),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      editable: hasWriteAccess,
      width: '240px',
      render: (text: string) => (
        <Text
          ellipsis={{
            tooltip: true,
          }}
          style={{
            maxWidth: '230px',
          }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      editable: hasWriteAccess,
      inputType: 'number',
      width: '70px',
      render: (value: number) => value || '-',
    },
    {
      title: 'Price Range',
      dataIndex: 'priceRange',
      width: '18%',
      render: (priceRange: { min: number; max: number }, record: PurchaseRequestItem) => {
        try {
          // Check both priceRange and priceRage fields
          const priceData = priceRange || record.priceRage || null;

          // If the price data is null or undefined, return a dash
          if (!priceData) return '-';

          // If the price data doesn't have min or max properties, return a dash
          if (typeof priceData.min !== 'number' || typeof priceData.max !== 'number') {
            return '-';
          }

          return (
            <Row justify={'center'}>
              <Button type="link" onClick={() => handlePriceRangeClick(record)}>
                {priceData.max === priceData.min ? `${priceData.max}€` : `${priceData.min}€ - ${priceData.max}€`}
              </Button>
            </Row>
          );
        } catch (error) {
          console.error('Error rendering price range:', error);
          return '-';
        }
      },
    },
  ];

  if (hasWriteAccess) {
    columns.push({
      title: '',
      dataIndex: 'operation',
      width: '60px',
      fixed: 'right',
      render: (_: any, record: PurchaseRequestItem) => (
        <Row justify="center" align="middle">
          <Popconfirm
            title="Are you sure you want to delete this item?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" icon={<DeleteIcon />} danger loading={isDeleting} />
          </Popconfirm>
        </Row>
      ),
    });
  }

  const components = {
    body: {
      row: (props: any) => (
        <EditableRow
          {...props}
          editingRow={editingRow}
          editingColumn={editingColumn}
          itemSelectorData={itemSelectorData}
          setEditingRow={setEditingRow}
          setEditingColumn={setEditingColumn}
          openAddItemModal={() => setOpenAddItem(true)}
        />
      ),
      cell: EditableCell,
    },
  };

  const mergedColumns = columns.map((col: any) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: PurchaseRequestItem) => ({
        record,
        dataIndex: col.dataIndex,
        editable: col.editable,
        inputType: col.inputType,
        handleSave,
      }),
    };
  });

  // Configure rowSelection object for checkboxes
  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[]) => {
      if (onSelectionChange) {
        onSelectionChange(selectedRowKeys);
      }
    },
    getCheckboxProps: (record: PurchaseRequestItem) => ({
      // Disable checkbox if item is not in system (not identified)
      disabled: record.isSystemItem === false || isFinalStatus,
    }),
  };

  return (
    <div className={styles.table_container}>
      {hasWriteAccess && (
        <Row justify={'space-between'} style={{ marginBottom: 16 }}>
          <Col>
            <Typography.Text strong style={{ fontSize: 18 }}>
              Items
            </Typography.Text>
            <HasAccess role={PERMISSIONS.items.item.write}>
              <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.write}>
                <Button
                  type={'link'}
                  style={{ color: '#05796B' }}
                  className={styles.statusBtn}
                  icon={<PlusOutlined />}
                  onClick={() => setOpenAddItem(true)}
                >
                  Add New
                </Button>
              </HasAccess>
            </HasAccess>
          </Col>
          <Col>
            <HasAccess role={PERMISSIONS.items.item.write}>
              <HasAccess role={PERMISSIONS.purchaseRequests.purchaseRequest.write}>
                <Button
                  type={'link'}
                  style={{ color: '#05796B' }}
                  className={styles.statusBtn}
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                >
                  Add Row
                </Button>
              </HasAccess>
            </HasAccess>
          </Col>
        </Row>
      )}

      <Table
        style={{ marginBottom: 50 }}
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={mergedColumns}
        rowSelection={rowSelection}
        pagination={false}
        scroll={{ x: 850 }}
      />

      {showPriceDrawer && selectedItem && (
        <SupplierPricesDrawer visible={showPriceDrawer} item={selectedItem} onClose={() => setShowPriceDrawer(false)} />
      )}
      <AddItemModal modalState={openAddItem} onModalStateChange={() => setOpenAddItem((os) => !os)} />
    </div>
  );
};
